/**
 * One player's input for one sim tick (docs/05). Quantized so it can go on the wire as-is:
 * the stick is i8, the camera yaw/pitch are u16 turns, and buttons are a bitmask.
 */
export interface Input {
  /** Stick, camera-relative: x = right, y = forward. Integers in [-127, 127]. */
  moveX: number;
  moveY: number;
  /** Camera yaw as a u16 fraction of a full turn (0 = looking toward +Z). */
  yaw: number;
  /** Camera pitch as an i16 fraction of a half turn (positive = up). */
  pitch: number;
  buttons: number;
}

export const Button = {
  Jump: 1 << 0,
  /** Shift / B: press = dodge, hold = sprint. */
  Dodge: 1 << 1,
  Brace: 1 << 2,
  Attack: 1 << 3,
  Aim: 1 << 4,
  LockOn: 1 << 5,
} as const;

export const NEUTRAL_INPUT: Input = Object.freeze({
  moveX: 0,
  moveY: 0,
  yaw: 0,
  pitch: 0,
  buttons: 0,
});

const TAU = Math.PI * 2;

export function quantizeYaw(radians: number): number {
  const turns = (((radians / TAU) % 1) + 1) % 1;
  return Math.round(turns * 65536) & 0xffff;
}

export function yawFromInput(yaw: number): number {
  return (yaw / 65536) * TAU;
}

export function quantizeStick(v: number): number {
  return Math.max(-127, Math.min(127, Math.round(v * 127)));
}

export function quantizePitch(radians: number): number {
  return Math.max(-32767, Math.min(32767, Math.round((radians / Math.PI) * 32767)));
}
